import Cookies from "js-cookie";
import CommonSteps from "../CommonSteps.json";
import locationsResponseWellness from "../assets/locationsResponseWellness.json";

export const pushToDataLayer = (event, data, toString) => {
  window.dataLayer = window.dataLayer || [];

  if (toString === undefined) {
    toString = false;
  }

  if (toString) {
    data = JSON.stringify(data);
  }

  window.dataLayer.push({ event, data });
};

export const sendGoogleAnalyticsEvent = (eventName, eventData) => {
  try {
    const gtag = window.gtag;
    if (typeof gtag === "function") {
      gtag("event", eventName, { payload: JSON.stringify(eventData) });
    } else {
      throw new Error("Google Analytics not loaded. Form Submit event not sent.");
    }
  } catch (e) {
    console.warn(e);
  }
};

export const validateField = (value, validationRules) => {
  if (validationRules.required != null) {
    const isValid = value != null && value.length > 0;
    if (!isValid) {
      return { isValid, error: validationRules.required };
    }
  }
  if (validationRules.minLength != null) {
    const isValid = value != null && value.length >= validationRules.minLength.value;
    if (!isValid) {
      return { isValid, error: validationRules.minLength.message };
    }
  }
  if (validationRules.regex != null) {
    const isValid = value.match(validationRules.regex.value) != null;
    if (!isValid) {
      return { isValid, error: validationRules.regex.message };
    }
  }
  return { isValid: true };
};

export const getTargetHostname = () => {
  const hostname = document.location.hostname;

  // Local domains
  if (hostname === "localhost" || hostname.startsWith("www-laseraway") || hostname.startsWith("get-laseraway")) {
    return "www-laseraway:9696";
  }

  // Dev domains: dev-www-gcp.laseraway.com, dev-get-gcp.laseraway.com, dev.laserawaywellness.com
  if (hostname.startsWith("dev")) {
    return "dev-www-gcp.laseraway.com";
  }

  // Staging domains: staging-www-gcp.laseraway.com, staging-get-gcp.laseraway.com, staging.laserawaywellness.com
  if (hostname.startsWith("staging")) {
    return "staging-www-gcp.laseraway.com";
  }

  // Default to production
  return "www.laseraway.com";
};

export const validateZipcode = async (zipcode, limit, wellnessLocations = false) => {
  try {
    const response = wellnessLocations ? null : await fetch(`https://${getTargetHostname()}/wp-json/zipcodesearch/v1/${zipcode}?limit=${limit}`);
    const locationsResponse = wellnessLocations ? locationsResponseWellness : await response.json();

    if (locationsResponse?.code !== "success") {
      return [];
    }

    return locationsResponse?.data?.map((location, index) => ({
      ...location,
      input_name: "your-city",
      value: location.wpsl_form_dropdown_value,
      selected: index === 0,
      index,
      coming_soon: location.wpsl_custom_status === "coming-soon",
    }));
  } catch (error) {
    displayAppError(error);
    return [];
  }
};

// this function is responsible to do any extra step after the initial load, usually select option from remote or dom
export const processAppData = async (form) => {
  const stepPromises = form.steps.map(async (step) => {
    if (step.commonStep === true) {
      step = { ...CommonSteps[step.id], ...step };
    }
    if (step.type === "input" && step.inputs.some((input) => input.type === "select" && ["remote", "dom"].includes(input.mode))) {
      const inputsPromises = step.inputs.map(async (input) => {
        if (input.type === "select" && input.mode === "remote") {
          try {
            const result = await fetch(input.remoteUrl);
            const options = await result.json();
            return { ...input, options };
          } catch (error) {
            return Promise.resolve(input);
          }
        }
        if (input.type === "select" && input.mode === "dom") {
          try {
            const selectSource = document.querySelector(input.querySelector);
            const options = Array.from(selectSource.children).map((child) => {
              const nodeName = child.nodeName;
              if (nodeName === "OPTGROUP") {
                const options = Array.from(child.children).map((subchild) => ({
                  text: subchild.text,
                  value: subchild.value,
                  classList: subchild.classList.toString(),
                }));
                return { text: child.label, type: "group", options };
              }
              return { text: child.text, value: child.value, classList: child.classList.toString() };
            });
            return { ...input, options };
          } catch (error) {
            return Promise.resolve(input);
          }
        }
        return Promise.resolve(input);
      });
      const inputs = await Promise.all(inputsPromises);
      return { ...step, inputs };
    }
    return Promise.resolve(step);
  });
  const steps = await Promise.all(stepPromises);
  return { ...form, steps };
};

const extractGoogleAnalyticsUtm = () => {
  const utmz = Cookies.get("__utmz");
  const utma = Cookies.get("__utma");

  let result = {};
  if (utmz != null) {
    const visitorSegs = utmz.split(".").slice(4).join(".").split("|");
    result = Object.fromEntries(visitorSegs.map((segment) => segment.split("=")));
  }

  if (utma != null) {
    const visitor = utma.split(".");
    const clientID = `${visitor[1]}.${visitor[2]}`;
    result.clientID = clientID;
  }

  return result;
};

export const loadUtmParams = () => {
  const utmParams = extractGoogleAnalyticsUtm();
  const queryParams = new URLSearchParams(location.search);
  const utmMapping = [
    { selector: ".utm-source", value: queryParams.get("utm_source") ?? utmParams.utmcsr },
    { selector: ".utm-medium", value: queryParams.get("utm_medium") ?? utmParams.utmcmd },
    { selector: ".client-id", value: utmParams.clientID },
    { selector: ".utm-campaign", value: queryParams.get("utm_campaign") ?? utmParams.utmccn },
    { selector: ".utm-content", value: queryParams.get("utm_content") ?? utmParams.utmcct },
  ];
  utmMapping
    .filter((entry) => entry.value != null)
    .forEach((entry) => {
      document.querySelectorAll(entry.selector).forEach((element) => (element.value = entry.value));
    });
};

// Fetch obfuscated lead preview
export const fetchLeadPreview = async (endpoint = "/wp-json/leads/v1/lookup-masked") => {
  try {
    const queryParams = new URLSearchParams(window.location.search);
    const uuid = queryParams.get("uid") || queryParams.get("uuid");
    if (!uuid) return null;

    const response = await fetch(`https://${getTargetHostname()}${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ data: uuid }),
    });
    if (!response.ok) return null;

    const payload = await response.json();

    const leadPreview = {
      id: payload?.id ?? null,
      uuid: payload?.uuid ?? null,
      name: payload?.name ?? "",
      email: payload?.contact?.email_masked ?? "",
      phone: payload?.contact?.phone_masked ?? "",
      locationId: payload?.location?.id ?? null,
    };

    return leadPreview;
  } catch {
    return null;
  }
};

export const duplicateLead = async (body, endpoint = "/wp-json/leadsHandler/v1/duplicate") => {
  const res = await fetch(`https://${getTargetHostname()}${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) return null;
  return res.json();
};
